import { v4 as uuidv4 } from 'uuid';
import {
  CommunicationType,
  Country,
  PhoneType,
  Status,
  VendorIntegrationType,
  PolicyAmount,
  RateType
} from '@grc/ui-shared/lib/components/models/enums/enums';
import { ICheckCallSetUp } from '../models/check-call.interface';
import { ICreateTransportViewModel } from '../models/create-transport.interface';
import { ILocationDetails } from '../models/location-details.interface';
import { IModeAndEquipmentDetails } from '../models/mode-and-equipment.interface';
import { IRate } from '../models/rate.interface';
import { IReference } from '../models/references-and-documents.interface';
import { ITransportationStop } from '../models/transportationstop.interface';
import { ITimeInterval } from '../models/settings.interface';

const initialLocationDetails: ILocationDetails = {
  id: '',
  tenantId: '',
  displayId: '',
  name: '',
  alias: '',
  generalEmailAddress: '',
  generalPhoneNumber: {
    phoneType: CommunicationType.Mobile,
    phoneNumber: '',
    displayOrder: 0
  },
  address: {
    streetAddress: [''],
    city: '',
    provinceState: '',
    postalZipCode: '',
    country: Country.UnitedStates
  },
  status: Status.Active
};

export const initialLocation: ILocationDetails = initialLocationDetails;

export const initialOriginDetails: ITransportationStop = {
  location: initialLocation,
  arrivalBegin: undefined,
  arrivalEnd: undefined,
  cutTime: undefined,
  departureBegin: undefined,
  departureEnd: undefined,
  trailerHandlingStrategy: undefined
};

export const initialDestinationDetails: ITransportationStop = {
  location: initialLocation,
  arrivalBegin: undefined,
  arrivalEnd: undefined,
  cutTime: undefined,
  departureBegin: undefined,
  departureEnd: undefined,
  trailerHandlingStrategy: undefined
};

export const initialModeAndEquipmentDetails: IModeAndEquipmentDetails = {
  id: '',
  transportationMode: '',
  equipmentType: '',
  trailerNumber: '',
  cabNumber: '',
  serviceType: undefined,
  serviceCharge: undefined,
  transportationModeId: '',
  equipmentTypeId: '',
  sealNumber: undefined,
  customsSealNumber: undefined
};

export const initialCheckCallSetUp: ICheckCallSetUp = {
  isStopArrivalRequired: false,
  isStopDepartureRequired: false,
  isBorderArrivalRequired: false,
  isBorderDepartureRequired: false,
  isInTransitCheckCallRequired: false,
  inTransitIntervalInMinutes: 30
};

export const initialReferences: IReference[] = [
  {
    referenceId: uuidv4(),
    referenceTypeId: '',
    referenceType: '',
    referenceNumber: '',
    document: {
      documentId: uuidv4(),
      documentContent: '',
      documentType: '',
      documentName: ''
    }
  }
];

export function formatDateToYYYYMMDD(date?: Date | string): string {
  if (!date) return '';

  let parsedDate: Date;

  if (typeof date === 'string') {
    parsedDate = new Date(date);
  } else {
    parsedDate = date;
  }

  if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();

  return `${year}-${month}-${day}`;
}

export const policyAmountValues = [
  {
    id: PolicyAmount.USD,
    policyAmount: 'USD'
  },
  {
    id: PolicyAmount.CAD,
    policyAmount: 'CAD'
  },
  {
    id: PolicyAmount.MexicanPeso,
    policyAmount: 'Mexican Peso'
  }
];

export const initialRateDetails: IRate = {
  freightItems: [
    {
      name: 'Freight Charges',
      amount: {
        value: 0,
        currencyId: ''
      }
    }
  ],
  fuelRate: {
    value: 0,
    currencyId: ''
  },
  accessorialItems: [
    {
      name: 'Accessorial Charges',
      amount: {
        value: 0,
        currencyId: ''
      }
    }
  ],
  carrier: '',
  totalRate: {
    value: 0,
    currencyId: ''
  },
  transitDays: 0
};

export const initialTransportationState: ICreateTransportViewModel = {
  transportId: '',
  transportTypeId: '',
  origin: initialOriginDetails,
  destination: initialDestinationDetails,
  modeAndEquipmentDetails: initialModeAndEquipmentDetails,
  ratedetails: initialRateDetails,
  trackingMethod: VendorIntegrationType.None,
  tenderInstructions: '',
  references: initialReferences,
  checkCallSetUp: initialCheckCallSetUp,
  originLocationDetails: undefined,
  driverDetails: {
    driverName: '',
    generalPhoneNumber: {
      phoneType: PhoneType.Mobile,
      phoneNumber: '',
      displayOrder: 1
    }
  },
  rateOnTender: RateType.None
};
export const importTypes = [
  { id: 1, text: 'Excel' },
  { id: 2, text: 'CSV' }
];


export enum HazmatAuthorized {
  Yes = 1,
  No = 2
}
export enum EnvironmentalCertification {
  Yes = 1,
  No = 2
}

export enum MinorityCarrier {
  Yes = 1,
  No = 2
}

export enum CheckCallInterval {
  Minutes15 = 1,
  Minutes30 = 2,
  Minutes45 = 3,
  Minutes60 = 4
}
export enum TenderMethods {
  EDI = 1,
  P44 = 2,
  Mobile = 3
}

export enum Shape {
  None = 0,
  Cubic = 1,
  Cylindrical = 2,
  Other = 3
}
export interface IScheduleType {
  isCustomWorkingHours: boolean;
  isOpen24Hours: boolean;
  dailySchedules: Record<DayOfWeek, IDailySchedule>;
}
export interface IDailySchedule {
  isDayOff: boolean;
  timeIntervals: ITimeInterval[];
}
export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6
}
export enum TenderRateType {
  None = 0,
  BaseRate = 1,
  AccessorialRate = 2,
  FuelSurcharge = 4,
  Taxes = 8,
  NoRate = 16
}

export enum Applicability {
  Optional = 1,
  Required = 2,
  NotApplicable = 3
}
export enum DocumentManagementType {
  Contract = 1,
  RulesTariff = 2,
  AccessorialList = 3,
  Any = 4
}

export const cancelLoadList = [
  {
    id: 1,
    name: 'Cancel Load'
  }
];
